// Runtime JS mirror of `showcase.content.ts`.
// Keep this file in sync so the CRA (JS) build can import it without TS module resolution quirks.
const showcaseImg = (fileName) => {
  // Absolute URL so it works regardless of router/base-path.
  // Also safely handles spaces in Windows screenshot filenames.
  const encoded = String(fileName).replaceAll(" ", "%20");
  return `${window.location.origin}/showcase/${encoded}`;
};

export const content = {
  app: {
    name: "Document Authoring Tool",
    tagline: "AI-assisted clinical document authoring and review workflows.",
    oneLiner:
      "Turn structured clinical intake + reference documents into editable drafts, governed workflows, and export-ready artifacts.",
    platformRole:
      "Authoring module for Protocol/ICF/SAP drafting, structured tables (SoA), exports, and collaborative review/approval.",
    status: "mvp",
    // Put images in `public/showcase/` and reference them as `/showcase/<file>`.
    appImage: showcaseImg("Screenshot 2026-05-28 170547.png"),
  },

  metrics: [
    {
      value: "50MB",
      label:
        "Per-file upload limit for AI parsing/validation (PDF/DOCX + images).",
    },
    {
      value: "7 days",
      label:
        "Default SAS availability window captured on approval snapshots (read links expire).",
    },
    {
      value: "4 states",
      label:
        "Workflow lifecycle in UI: Draft → Co-authoring → Review → Approval.",
    },
  ],

  problem: {
    statement:
      "Clinical documents are high-stakes and slow to produce: teams chase inputs across forms and reference files, then repeat cycles of drafting, review, redaction, export, and handoffs.",
    whoFeelsIt:
      "Clinical operations, medical writing, regulatory, and cross-functional study teams who need a fast, traceable path from intake to an approval-ready document set.",
  },

  approach: {
    summary:
      "Capture structured intake, generate drafts and structured tables, then run a governed workflow for collaboration, review, and approval—producing exportable, shareable artifacts along the way.",
    steps: [
      {
        title: "Capture canonical inputs",
        description:
          "Projects and clinical intake provide structured inputs; uploaded reference documents can be validated and parsed into JSON for downstream use.",
      },
      {
        title: "Draft and iterate in an editor",
        description:
          "Protocol/ICF/SAP content is edited as sections, versioned, and can be regenerated/validated via dedicated endpoints.",
      },
      {
        title: "Generate structured deliverables",
        description:
          "Schedule of Activities (SoA) is generated and versioned, with export/import paths to Excel and XML.",
      },
      {
        title: "Run a real review/approval workflow",
        description:
          "OnlyOffice-backed workflows manage roles, comments, state transitions, and immutable approval snapshots.",
      },
      {
        title: "Export, redact, and distribute",
        description:
          "Generate PDFs, store export records, request redaction suggestions, build redacted artifacts, and email deliverables when needed.",
      },
    ],
  },

  capabilities: [
    {
      title: "Protocol drafting: generate sections from the latest Clinical Intake",
      description:
        "Kick off section-by-section generation using the most recent structured intake, then refine output in an editor built for iteration.",
      status: "mvp",
      marquee: true,
      evidence:
        "document_editor_new - Copy/etmf-client/src/protocol/components/DocumentEditor.jsx (generation + editor orchestration) + document_editor_new - Copy/etmf-server/src/routes/ai.routes.js (POST /get-result)",
    },
    {
      title: "Governed document workflow with co-authoring, review, and approval",
      description:
        "Move a draft through co-authoring, structured review, and final approval—capturing comments, history, and role-based actions throughout.",
      status: "mvp",
      marquee: true,
      evidence:
        "document_editor_new - Copy/etmf-client/src/components/documentEditor/OnlyOfficeWorkflow.jsx + document_editor_new - Copy/etmf-server/src/routes/documentWorkflow.routes.js",
    },
    {
      title: "Schedule of Activities (SoA) generation + versioning",
      description:
        "Generate a structured SoA from intake and protocol context, track versions, regenerate with new prompts, and preserve change history.",
      status: "mvp",
      marquee: true,
      evidence:
        "document_editor_new - Copy/etmf-server/src/controllers/scheduleOfActivities.controller.js + document_editor_new - Copy/etmf-client/src/protocol/components/DocumentEditor.jsx",
    },
    {
      title: "Export-ready outputs: Word + PDF workflows",
      description:
        "Create exportable artifacts for downstream sharing and review, including PDF preview paths used in workflows.",
      status: "mvp",
      marquee: true,
      evidence:
        "document_editor_new - Copy/etmf-client/src/protocol/components/DocumentEditor.jsx + document_editor_new - Copy/etmf-server/src/controllers/documentExport.controller.js",
    },
    {
      title: "Audit Trail",
      description:
        "Every create/update/delete on protocol, ICF, and SAP content is captured with actor, timestamp, whole-document version, AI-drafted/edited provenance, and a before/after diff — immutable by design (21 CFR Part 11).",
      status: "mvp",
      marquee: true,
      evidence:
        "document_editor_new - Copy/etmf-client/src/pages/AuditTrail.jsx + document_editor_new - Copy/etmf-server/src/models/auditLog.model.js + document_editor_new - Copy/etmf-server/src/plugins/auditable.plugin.js",
    },

    {
      title: "Project organization: create, browse, and manage projects and studies along with study creation",
      description:
        "Organize work by project and study, with UI for browsing and switching context as teams move between authoring tasks. Initial study resgistration form.",
      status: "live",
      image: showcaseImg("Screenshot 2026-05-28 170621.png"),
      evidence:
        "document_editor_new - Copy/etmf-client/src/components/layout/MainLayout.js + document_editor_new - Copy/etmf-server/src/routes/project.routes.js",
    },
    {
      title: "Clinical Intake: structured capture with change-control utilities and protocol section page",
      description:
        "Collect structured study inputs and support workflow locking, unlock-with-reason, and change detection against a locked snapshot.It shows Complete protocol with all the sections based on ICH M11 guidelines",
      status: "mvp",
      image: showcaseImg("Screenshot 2026-05-28 170736.png"),
      evidence:
        "document_editor_new - Copy/etmf-server/src/routes/clinicalIntake.js",
    },
    {
      title: "ICF Editor: fetch the data from generated protocol and generate the ICF content based on that data",
      description:
        "It contains all the section of informed consent form based on ICH M11 guidelines and fetch the data from generated protocol to generate the content of those sections.",
      status: "mvp",
      image: showcaseImg("Screenshot 2026-05-28 170855.png"),
      evidence:
        "document_editor_new - Copy/etmf-server/src/routes/ai.routes.js (POST /validate-documents) + document_editor_new - Copy/etmf-server/src/controllers/ai.controller.js",
    },

    {
      title: "Translations ICF: Translations of the generated ICF content into different languages based on the language selected by the user",
      description:
        "Generate the translations of the generated ICF content into different languages based on the language selected by the user.",
      status: "mvp",
      image: showcaseImg("Screenshot 2026-05-28 170947.png"),
      evidence:
        "document_editor_new - Copy/etmf-server/src/routes/documentEditor.routes.js + document_editor_new - Copy/etmf-client/src/components/versions/VersionHistory.jsx",
    },
    {
      title: "Design Studio for the generation of clinical trials documents",
      description:
        "Generate the clinical trial documents based on the user input and the reference documents uploaded by the user in a session-based flow where the user can have a conversation with the system to refine the output.",
      status: "mvp",
      image: showcaseImg("Screenshot 2026-05-28 171143.png"),
      evidence:
        "document_editor_new - Copy/etmf-server/src/routes/styleCompliance.routes.js",
    },
    {
      title: "Review workflow dashboard with document status, activity history, and comment threads",
      description:
        "Overview dashboard for document workflows showing current status, activity history, and access to comment threads to support review and approval collaboration.",
      status: "mvp",
      image: showcaseImg("Screenshot 2026-05-28 171334.png"),
      evidence:
        "document_editor_new - Copy/etmf-server/src/services/workflowEmail.service.js + document_editor_new - Copy/etmf-server/src/controllers/documentExport.controller.js",
    },

    {
      title: "Reviewer page with document status, activity history, and comment threads",
      description:
        "Reviewer page showing document status, activity history, and reviewer can comment on the document like protocol.",
      status: "mvp",
      image: showcaseImg("Screenshot 2026-05-28 171434.png"),
      evidence:
        "document_editor_new - Copy/etmf-server/src/routes/documentWorkflow.routes.js (GET /:documentId/onlyoffice-editor) + document_editor_new - Copy/etmf-server/src/services/onlyoffice.service.js",
    },
    {
      title: "Immutable approval snapshot for approved documents",
      description:
        "On final approval, capture an immutable snapshot of workflow state, roster, and protocol sections for auditability and future reference.",
      status: "mvp",
      evidence:
        "document_editor_new - Copy/etmf-server/src/routes/documentWorkflow.routes.js (coordinator-final-approve snapshot creation) + document_editor_new - Copy/etmf-server/src/models/approvedProtocolSnapshot.model.js",
    },

    {
      title: "SoA exports: Excel export/import + M11 XML export and validation",
      description:
        "Move between structured SoA and external tooling: export/import Excel, produce M11 XML, and validate outputs.",
      status: "mvp",
      evidence:
        "document_editor_new - Copy/etmf-server/src/controllers/scheduleOfActivities.controller.js (export/import + M11 XML) + document_editor_new - Copy/etmf-server/src/services/m11xml.service.js",
    },

    {
      title: "Design Studio: chat + structured plan + section regeneration (session-based)",
      description:
        "Run a session-based design flow: keep conversation context, upload reference docs, update structure, and regenerate targeted sections.",
      status: "mvp",
      evidence:
        "document_editor_new - Copy/etmf-server/src/designStudio/routes/designStudio.routes.js",
    },
    {
      title: "Templates: prompt packs + template-driven generation",
      description:
        "Use template-specific prompts and extraction utilities to generate sections with consistent structure and tone.",
      status: "mvp",
      evidence:
        "document_editor_new - Copy/etmf-server/src/templates/routes/template.routes.js",
    },

    {
      title: "Protocol registration utilities (USDM mapping + translations + attachments)",
      description:
        "Support registry-facing outputs with canonical dataset extraction, translation services, and USDM mapping utilities.",
      status: "mvp",
      evidence:
        "document_editor_new - Copy/etmf-server/src/protocolRegistration/routes/protocolRegistration.routes.js + document_editor_new - Copy/etmf-server/src/ddf/usdm-mapper/mapper.js",
    },
    {
      title: "Knowledge graph utilities for validation and sync (optional)",
      description:
        "Extract entities, validate sections, and sync to a knowledge graph to support consistency checks and downstream QA workflows.",
      status: "dev",
      evidence:
        "document_editor_new - Copy/etmf-server/src/routes/validation.routes.js",
    },
    {
      title: "ADK agent workflow endpoints (optional)",
      description:
        "Kick off agent workflows with approval steps and pollable status endpoints for longer-running operations.",
      status: "dev",
      evidence:
        "document_editor_new - Copy/etmf-server/src/routes/adk.routes.js",
    },
    {
      title: "Feasibility module endpoints including Part 11 signing (optional)",
      description:
        "Capture feasibility inputs and support an e-signature flow for regulated contexts where required.",
      status: "dev",
      evidence:
        "document_editor_new - Copy/etmf-server/src/feasibility/routes/feasibility.routes.js",
    },
  ],

  platformFit: {
    touches: [
      "Clinical intake → protocol draft generation",
      "Collaborative authoring and review workflows",
      "Structured tables (SoA) with export interoperability",
      "Redaction + distribution of sensitive artifacts",
      "Registry/USDM mapping utilities",
    ],
    integrations: [
      "Google Gemini (generation, parsing, validation)",
      "Azure Blob Storage (uploads and artifact storage)",
      "OnlyOffice (workflow editing/config)",
      "Email/SMTP (notifications and delivery)",
      "Microsoft OAuth callback (conversion flow support)",
    ],
  },

  differentiators: [
    "End-to-end flow from structured intake to governed approval—without losing traceability.",
    "Sections + workflows + artifacts are first-class: versioning, comments, history, and exports are built into the surface area.",
    "Structured outputs (SoA) are treated as editable, versioned data—not screenshots—so teams can export/import and validate.",
    "Approval snapshots capture immutable state at the moment it matters, supporting audit-friendly downstream processes.",
  ],

  whatsNext: [
    "Hub IAM integration: consume Hub identity + canonical permissions while retaining module-local app sessions (V1 target).",
    "Back-end authorization hardening: enforce permission checks on sensitive endpoints consistently (UI gating is not security).",
    "Workflow AI-update completion audit: verify end-to-end requestAIUpdate wiring and add test coverage where missing.",
    "Operational KPIs dashboard for draft time, generation latency, export success rate, and reviewer cycle time.",
  ],
};

export default content;

